import { UNITS, type ElitePlanOfferType } from './misc.types'
import { TaxCategories } from './mutual-fund-detail.types'

export enum RebalanceType {
  LUMPSUM = 'LUMPSUM',
  SWP = 'SWP',
  LUMPSUM_SIP = 'LUMPSUM_SIP',
  SWP_SIP = 'SWP_SIP',
  LUMPSUM_HIGH_EXPOSURE = 'LUMPSUM_HIGH_EXPOSURE',
  SWP_HIGH_EXPOSURE = 'SWP_HIGH_EXPOSURE',
  SIP = 'SIP',
  LUMPSUM_ELSS = 'LUMPSUM_ELSS',
  SWP_ELSS = 'SWP_ELSS',
  LUMPSUM_SIP_ELSS = 'LUMPSUM_SIP_ELSS',
  SWP_SIP_ELSS = 'SWP_SIP_ELSS',
  LUMPSUM_HIGH_EXPOSURE_ELSS = 'LUMPSUM_HIGH_EXPOSURE_ELSS',
  SWP_HIGH_EXPOSURE_ELSS = 'SWP_HIGH_EXPOSURE_ELSS',
  SIP_ELSS = 'SIP_ELSS'
}

// For nested tax info objects (STCG and LTCG share this structure)
export type TaxInfo = {
  gains: number
  investedAmount: number
  taxRate: number
  taxableAmount: number
}

// For the reasonsToSwitch object inside a suggestion
export type ReasonsToSwitch = {
  xirrDiff: number
  xirrDiffDurationInMonths: number
}

// For each object in the switchSuggestions array
export type SwitchSuggestion = {
  isin: string
  name: string
  switchPercentage: number
  fundNature: string
  fundCategory: string
  mfLogoUrl: string
  powerRank: number
  performanceRating: string
  purchaseMode: string
  investedAmount: number | null
  currentWealth: number | null
  reasonsToSwitch: ReasonsToSwitch
  // Additional fields used in frontend (may not be in backend response)
  missedGains?: number
  highExposure?: boolean
  sipAmount?: number
  isRegular?: boolean
  exclusionReason?: string
  formType?: string
}

// For the stepsToRebalance object
export type StepsToRebalance = {
  swpAmountPerMonth: number | null
  swpDurationInMonths: number
}

// For the main taxDetails object
export type TaxDetails = {
  stcgTaxInfo: TaxInfo
  ltcgTaxInfo: TaxInfo
  exitLoad: number
  currentValue: number
  netTaxLiability: number
  taxCategory: `${TaxCategories}`
}

// The main, top-level type for the entire JSON object
export type FundRebalanceData = {
  rebalanceTransferType: string
  rebalanceStrategyType: string | RebalanceType // Backend returns string, but frontend uses enum
  isin: string
  name: string
  fundNature: string
  fundCategory: string
  mfLogoUrl: string
  powerRank: number
  performanceRating: string
  purchaseMode: string
  missedGains: number
  investedAmount: number
  currentWealth: number
  isHighExposureFund: boolean
  highExposureThreshold: number
  highExposureTransferAmount: number
  isElss: boolean
  exclusionReason: string
  switchSuggestions: SwitchSuggestion[]
  stepsToRebalance: StepsToRebalance
  taxDetails: TaxDetails
  missedGainsAsOfDate: number
  recommendationDate: number
  // Additional fields used in frontend (derived/computed, may not be in backend response)
  highExposure?: boolean // Alias for isHighExposureFund
  sipAmount?: number
  isRegular?: boolean // Derived from purchaseMode === 'REGULAR'
  formType?: string // Usually same as performanceRating
  // Legacy fields for backward compatibility (not in backend response)
  elss?: boolean
  highExposureFund?: boolean
  recommendationStatus?: 'NEW' | 'UPDATED' | 'SAME' | 'INVALIDATED'
  distributionStatus?: 'INCOME' | 'ACCUMULATED'
  recommendationStatusReason?: string
  recommendationStatusReasonHeader?: string
}

// For funds in the summary response
export type RebalanceSummaryFund = {
  isin: string
  name: string
  fundNature: string
  fundCategory: string
  purchaseMode: string
  powerRank: number
  performanceRating: string
  missedGains: number
  highExposure: boolean
  mfLogoUrl: string
  sipAmount?: number
  showConsentBottomSheet?: boolean
  exclusionReason?: string
}

// Response type for power rebalance summary endpoint
export type PowerRebalanceSummaryResponse = {
  rebalancedFunds: RebalanceSummaryFund[]
  fundsExcludedFromRebalancing: RebalanceSummaryFund[]
  rebalanceExecutedFunds: RebalanceSummaryFund[]
  rebalanceEvaluationRefetchDate: number
  riskProfile: string
  planStartDate: number
  planDurationInMonths: number
  throughFamilyAccess?: boolean
  primaryUserName?: string
  secondaryUserPhoneNumber?: string
  secondaryUserName?: string
  familyConsentText?: string
}

// Response type for save risk profile endpoint
export type SaveRiskProfileResponse = {
  success: boolean
  message?: string
}

// OTP verification types for rebalance questionnaire
export interface RebalanceQuestionnaireOTPTriggerResponse {}

export interface RebalanceQuestionnaireOTPVerifyPayload {
  postBackParams: {
    familyConsentType: 'REBALANCE_RISK_PROFILE'
  }
  inputData: {
    authCode: string
  }
}

export interface RebalanceQuestionnaireOTPVerifyResponse {
  verified: boolean
  message?: string
}

export type CountdownTime = { days: number; hours: number; minutes: number; seconds: number; hasEnded: boolean }

export type StatusResponse = {
  timeToRebalancePlanDrop: number
  numberOfFundsToRebalance: number
  missedGains: number
  rebalanceAvailable: boolean
  firstPlanMonth: number
  nextPlanMonth: number
  riskProfile: string | null
  userEligibleForRebalance: boolean
  numberOfFundsExcludedFromRebalance: number
  rebalanceSnapshotDate?: number
  planDurationInMonths: number
  rebalancePlanCount: number
  throughFamilyAccess?: boolean
  primaryUserName?: string
  secondaryUserPhoneNumber?: string
  secondaryUserName?: string
  familyConsentText?: string
  userSubscriptionDetails: {
    specialOffering?: boolean | null
    signupOffer?: boolean | null
    offerStartTime?: number | null
    offerExpiryTime?: number | null
    offerType?: ElitePlanOfferType | null
    membershipFees?: {
      amount: number
      unit: `${UNITS.RUPEES}`
    }
  }
}
